import type { MouseEvent } from "react";
import CheckRounded from "@mui/icons-material/CheckRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import DeleteRounded from "@mui/icons-material/DeleteRounded";
import EditRounded from "@mui/icons-material/EditRounded";
import {
  IconButton,
  ListItemText,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import cx from "classnames";
import { formatDistanceToNow } from "date-fns";
import TruncationAwareTooltip from "src/components/general/TruncationAwareTooltip/TruncationAwareTooltip";
import { SESSION_TITLE_MAX_INPUT_LENGTH } from "../../../../AiToolboxSection.constants";
import type { ChatSession } from "../../../../AiToolboxSection.types";
import type { UseInlineEditReturn } from "../../ChatHistory.hooks";

interface ChatHistorySessionMenuItemProps {
  session: ChatSession;
  activeSessionId: string | null;
  isLastItem: boolean;
  inlineEdit: UseInlineEditReturn;
  onSelectSession: (sessionId: string) => void;
  onStartEdit: (sessionId: string, currentTitle: string) => void;
  onDeleteSession: (sessionId: string, sessionTitle: string) => void;
}

export const ChatHistorySessionMenuItem = ({
  session,
  activeSessionId,
  isLastItem,
  inlineEdit,
  onSelectSession,
  onStartEdit,
  onDeleteSession,
}: ChatHistorySessionMenuItemProps) => {
  const isEditing = inlineEdit.isEditing(session.id);

  const handleSelectClick = () => {
    onSelectSession(session.id);
  };

  const handleStartEditClick = (e: MouseEvent) => {
    e.stopPropagation();
    onStartEdit(session.id, session.title);
  };

  const handleDeleteClick = (e: MouseEvent) => {
    e.stopPropagation();
    onDeleteSession(session.id, session.title);
  };

  const handleSaveClick = (e: MouseEvent) => {
    e.stopPropagation();
    inlineEdit.saveEdit();
  };

  const handleCancelClick = (e: MouseEvent) => {
    e.stopPropagation();
    inlineEdit.cancelEdit();
  };

  const handleStopPropagation = (e: MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <MenuItem
      selected={session.id === activeSessionId}
      onClick={isEditing ? undefined : handleSelectClick}
      {...(!isEditing
        ? { "data-ph-capture-attribute-action": "select_ai_session" }
        : {})}
      className={cx("group flex justify-between items-center overflow-hidden", {
        "border-b border-divider": !isLastItem,
      })}
    >
      {isEditing ? (
        <>
          <TextField
            inputRef={inlineEdit.inputRef}
            value={inlineEdit.editingValue}
            onChange={(e) => inlineEdit.setEditingValue(e.target.value)}
            onKeyDown={inlineEdit.handleKeyDown}
            onBlur={() => inlineEdit.saveEdit()}
            size="small"
            variant="outlined"
            placeholder="Enter title..."
            className="flex-1 min-w-0"
            onClick={handleStopPropagation}
            slotProps={{
              htmlInput: {
                // A new name stops at 100 characters. A saved name that is already
                // longer must fit in the field, or the next save would chop it.
                maxLength: Math.max(
                  SESSION_TITLE_MAX_INPUT_LENGTH,
                  session.title.length
                ),
              },
            }}
          />
          <div className="flex items-center gap-0.5 ml-1 shrink-0">
            <Tooltip title="Save">
              <IconButton
                size="small"
                onClick={handleSaveClick}
                disabled={!inlineEdit.editingValue.trim()}
                className="text-success-main hover:bg-success-lightest"
                data-ph-capture-attribute-action="save_ai_session_title"
              >
                <CheckRounded fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel">
              <IconButton
                size="small"
                onClick={handleCancelClick}
                className="text-error-main hover:bg-error-lightest"
                data-ph-capture-attribute-action="cancel_ai_session_title_edit"
              >
                <CloseRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </>
      ) : (
        <>
          <ListItemText
            primary={
              // The row clips a long name. The saved name is still the whole prompt.
              <TruncationAwareTooltip title={session.title} arrow disableInteractive>
                {session.title}
              </TruncationAwareTooltip>
            }
            secondary={formatDistanceToNow(session.updatedAt, { addSuffix: true })}
            slotProps={{
              primary: {
                component: "div",
                className: cx("font-semibold", {
                  "text-text-primary": session.id === activeSessionId,
                }),
              },
              secondary: {
                className: "text-xs truncate",
              },
            }}
          />
          <div className="flex items-center gap-1 ml-2 shrink-0 opacity-0 -mr-18 group-hover:-mr-2 group-hover:opacity-100 transition-all duration-200 ease-in-out">
            <IconButton
              size="small"
              onClick={handleStartEditClick}
              data-ph-capture-attribute-action="edit_ai_session_title"
            >
              <EditRounded fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleDeleteClick}
              data-ph-capture-attribute-action="delete_ai_session"
            >
              <DeleteRounded fontSize="small" />
            </IconButton>
          </div>
        </>
      )}
    </MenuItem>
  );
};
